/**
 * ReconnectUI - Handles connection loss and recovery UI.
 *
 * Responsibilities:
 * - Detect connection loss (reconnecting state)
 * - Display reconnecting UI with status message
 * - Recover synchronized state via ReconnectState message
 * - Restore correct view (preparation/combat) after reconnection
 * - Handle connection errors
 * - Provide render data for UI binding
 *
 * The backend is authoritative; this component only renders reconnection state
 * that the server sends via connection state changes and ReconnectState messages.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import { ConnectionState } from '../models/ConnectionState';
import { ReconnectState } from '../../shared/messages/MessageContracts';

// ─── Reconnection Render Data ───────────────────────────────────────────────

/**
 * The current reconnection status.
 */
export type ReconnectStatus = 'connected' | 'reconnecting' | 'error' | 'disconnected' | 'recovered';

/**
 * Render data for the reconnect UI.
 */
export interface ReconnectRenderData {
  /** Whether the reconnect UI overlay should be visible. */
  isVisible: boolean;

  /** Current connection status. */
  status: ReconnectStatus;

  /** Whether the connection is currently active (connected). */
  isConnected: boolean;

  /** Whether the connection is currently reconnecting. */
  isReconnecting: boolean;

  /** Whether there was a connection error. */
  hasError: boolean;

  /** Error message if there was a connection error. */
  errorMessage: string;

  /** Human-readable status message for display. */
  statusMessage: string;

  /** Whether the reconnection was successful and state was recovered. */
  isRecovered: boolean;

  /** Match ID being reconnected to (or null if no match). */
  matchId: string | null;

  /** Phase at time of reconnection (preparation, combat, etc.). */
  reconnectedPhase: string | null;

  /** Current round at time of reconnection. */
  reconnectedRound: number | null;

  /** Whether the match was in progress at time of reconnection. */
  wasMatchActive: boolean;

  /** Whether player-action controls should be hidden during reconnection. */
  hidePlayerControls: boolean;
}

/**
 * Callback invoked when render data changes.
 */
export type ReconnectRenderCallback = (data: ReconnectRenderData) => void;

/**
 * Callback invoked when reconnection is successful.
 */
export type ReconnectedCallback = (reconnectState: ReconnectState) => void;

/**
 * Callback invoked when connection is lost.
 */
export type ConnectionLostCallback = () => void;

// ─── Reconnect UI ───────────────────────────────────────────────────────────

export class ReconnectUI {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: ReconnectRenderCallback[] = [];
  private _reconnectedCallbacks: ReconnectedCallback[] = [];
  private _connectionLostCallbacks: ConnectionLostCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;

  // Internal state
  private _connectionState: ConnectionState = 'disconnected';
  private _lastError: string = '';
  private _lastReconnectState: ReconnectState | null = null;
  private _wasConnected: boolean = false;

  constructor(appShell: AppShell) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data. */
  get renderData(): ReconnectRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the reconnect UI is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  /** Returns the current connection status. */
  get status(): ReconnectStatus {
    return this._buildRenderData().status;
  }

  /** Returns true if the connection is active. */
  get isConnected(): boolean {
    return this._buildRenderData().isConnected;
  }

  /** Returns true if currently reconnecting. */
  get isReconnecting(): boolean {
    return this._buildRenderData().isReconnecting;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the reconnect UI and start listening for connection state changes.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;
    this._connectionState = this._appShell.connectionState;

    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    this._notifyRenderCallbacks();
  }

  /**
   * Hide the reconnect UI and stop listening for events.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  /**
   * Reset the reconnect UI state.
   */
  reset(): void {
    this._connectionState = 'disconnected';
    this._lastError = '';
    this._lastReconnectState = null;
    this._wasConnected = false;
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   * Returns an unsubscribe function.
   */
  onRenderChange(callback: ReconnectRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to successful reconnection events.
   * Returns an unsubscribe function.
   */
  onReconnected(callback: ReconnectedCallback): () => void {
    this._reconnectedCallbacks.push(callback);
    return () => {
      const index = this._reconnectedCallbacks.indexOf(callback);
      if (index !== -1) {
        this._reconnectedCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to connection lost events.
   * Returns an unsubscribe function.
   */
  onConnectionLost(callback: ConnectionLostCallback): () => void {
    this._connectionLostCallbacks.push(callback);
    return () => {
      const index = this._connectionLostCallbacks.indexOf(callback);
      if (index !== -1) {
        this._connectionLostCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Manual State Setting ────────────────────────────────────────────────

  /**
   * Manually set the connection state (for testing).
   */
  setConnectionState(state: ConnectionState, error?: string): void {
    const previousState = this._connectionState;
    this._connectionState = state;
    if (error) {
      this._lastError = error;
    }

    if (state === 'reconnecting' && previousState === 'connected') {
      this._notifyConnectionLostCallbacks();
    }

    if (state === 'connected' && previousState === 'reconnecting') {
      this._wasConnected = true;
    }

    this._notifyRenderCallbacks();
  }

  /**
   * Manually set the reconnection state (for testing).
   */
  setReconnectState(reconnectState: ReconnectState): void {
    this._lastReconnectState = reconnectState;
    this._connectionState = 'connected';
    this._notifyReconnectedCallbacks(reconnectState);
    this._notifyRenderCallbacks();
  }

  // ─── Internal Event Handling ─────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'connectionChange': {
        const data = event.data as { previousState: ConnectionState; currentState: ConnectionState; error?: string };
        const previousState = this._connectionState;
        this._connectionState = data.currentState;
        if (data.error) {
          this._lastError = data.error;
        }

        // Detect connection loss
        if (data.currentState === 'reconnecting' && previousState === 'connected') {
          this._notifyConnectionLostCallbacks();
        }

        // Detect successful reconnection
        if (data.currentState === 'connected' && previousState === 'reconnecting') {
          this._wasConnected = true;
        }

        this._notifyRenderCallbacks();
        break;
      }
      case 'stateChange': {
        // Update render data on state changes (synchronized state after reconnect)
        this._notifyRenderCallbacks();
        break;
      }
    }
  }

  // ─── Internal ────────────────────────────────────────────────────────────

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _notifyReconnectedCallbacks(reconnectState: ReconnectState): void {
    for (const callback of this._reconnectedCallbacks) {
      callback(reconnectState);
    }
  }

  private _notifyConnectionLostCallbacks(): void {
    for (const callback of this._connectionLostCallbacks) {
      callback();
    }
  }

  private _buildRenderData(): ReconnectRenderData {
    const cs = this._connectionState;
    const isReconnecting = cs === 'reconnecting';
    const isConnected = cs === 'connected';
    const hasError = cs === 'error';
    const isRecovered = this._lastReconnectState !== null && isConnected;

    // Determine status
    let status: ReconnectStatus;
    if (isRecovered) {
      status = 'recovered';
    } else if (hasError) {
      status = 'error';
    } else if (isReconnecting) {
      status = 'reconnecting';
    } else if (isConnected) {
      status = 'connected';
    } else {
      status = 'disconnected';
    }

    // Build status message
    const statusMessage = this._buildStatusMessage(status, this._lastError, this._lastReconnectState);

    // Determine visibility: show overlay when reconnecting or error
    const isVisible = isReconnecting || hasError;

    // Determine whether to hide player controls during reconnection
    const hidePlayerControls = isReconnecting || hasError;

    // Extract reconnection info
    const reconnectState = this._lastReconnectState;
    const matchId = reconnectState ? reconnectState.matchId : null;
    const reconnectedPhase = reconnectState ? reconnectState.phase : null;
    const reconnectedRound = reconnectState ? reconnectState.currentRound : null;
    const wasMatchActive = reconnectState ? !reconnectState.gameOver && reconnectState.phase !== '' : false;

    return {
      isVisible,
      status,
      isConnected,
      isReconnecting,
      hasError,
      errorMessage: this._lastError,
      statusMessage,
      isRecovered,
      matchId,
      reconnectedPhase,
      reconnectedRound,
      wasMatchActive,
      hidePlayerControls,
    };
  }

  private _buildStatusMessage(
    status: ReconnectStatus,
    error: string,
    reconnectState: ReconnectState | null,
  ): string {
    switch (status) {
      case 'reconnecting':
        return 'Connection lost. Reconnecting...';
      case 'error':
        return error ? `Connection error: ${error}` : 'Connection error. Retrying...';
      case 'recovered':
        if (reconnectState) {
          const phaseText = reconnectState.phase ? ` (phase: ${reconnectState.phase})` : '';
          const roundText = reconnectState.currentRound ? `, round ${reconnectState.currentRound}` : '';
          return `Connection restored.${phaseText}${roundText}`;
        }
        return 'Connection restored.';
      case 'connected':
        return '';
      case 'disconnected':
        return '';
      default:
        return '';
    }
  }
}
